import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Sparkles, Users, Building2, Tag, Gift } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  priceId: string;
  productId: string;
  description: string;
  features: string[];
  icon: React.ElementType;
  popular?: boolean;
  trialDays?: number;
  trialLabel?: string;
}

export const subscriptionTiers: SubscriptionTier[] = [
  {
    id: "free",
    name: "Free",
    price: 0,
    priceId: "",
    productId: "",
    description: "Get started — no credit card",
    icon: Gift,
    features: [
      "1 team member",
      "Up to 25 clients",
      "10 invoices / month",
      "Core CRM & scheduling",
      "Detail Pro branding on docs",
      "Community support",
    ],
  },
  {
    id: "starter",
    name: "Starter",
    price: 29,
    priceId: "price_1SbVFAIdDfjPlKRTKVtfzEyn",
    productId: "prod_TYcU05edT1Uk8r",
    description: "Essential tools for solopreneurs",
    icon: Sparkles,
    trialDays: 90,
    trialLabel: "3 MONTHS FREE",
    features: [
      "Up to 3 team members",
      "Basic CRM & client management",
      "Invoicing & estimates",
      "Scheduling & time tracking",
      "Basic reporting",
      "Email support",
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: 79,
    priceId: "price_1SbVFLIdDfjPlKRTSj4H0tCZ",
    productId: "prod_TYcUASLkIY2e6c",
    description: "Full-featured for growing teams",
    icon: Users,
    popular: true,
    trialDays: 180,
    trialLabel: "6 MONTHS FREE",
    features: [
      "Up to 10 team members",
      "Advanced CRM with lead scoring",
      "All invoicing features",
      "Client & employee portals",
      "AI business consultant",
      "AutoPilot agent (basic)",
      "SMS & social messaging",
      "Priority email support",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: 149,
    priceId: "price_1SbVFmIdDfjPlKRTjA4SqPHS",
    productId: "prod_TYcUhqCGkf1Am0",
    description: "Enterprise-grade for scaling",
    icon: Building2,
    trialDays: 365,
    trialLabel: "1 YEAR FREE",
    features: [
      "Unlimited team members",
      "Everything in Professional",
      "AutoPilot full agent suite",
      "White-label branding",
      "Custom integrations",
      "Advanced analytics & API",
      "Dedicated account manager",
      "Phone & chat support",
    ],
  },
];

interface TierSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTier: (tier: SubscriptionTier, discountCode?: string) => void;
}

const TierSelectionDialog = ({
  open,
  onOpenChange,
  onSelectTier,
}: TierSelectionDialogProps) => {
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [discountCode, setDiscountCode] = useState("");
  const [showDiscountInput, setShowDiscountInput] = useState(false);

  const isValidDiscount = discountCode === "Addary@2006" || discountCode === "Saab";

  const handleContinue = () => {
    const tier = subscriptionTiers.find((t) => t.id === selectedTier);
    if (tier) {
      onSelectTier(tier, discountCode || undefined);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center">
            Choose Your Plan
          </DialogTitle>
          <p className="text-center text-muted-foreground">
            Pick a paid plan and get an extended free trial — no charge until your trial ends.
          </p>
        </DialogHeader>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 py-6">
          {subscriptionTiers.map((tier) => {
            const Icon = tier.icon;
            const isSelected = selectedTier === tier.id;

            return (
              <Card
                key={tier.id}
                className={cn(
                  "relative p-6 cursor-pointer transition-all hover:shadow-lg",
                  isSelected && "ring-2 ring-primary shadow-lg",
                  tier.popular && "border-primary"
                )}
                onClick={() => setSelectedTier(tier.id)}
              >
                {tier.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                    Most Popular
                  </Badge>
                )}

                {tier.trialLabel && (
                  <Badge
                    variant="secondary"
                    className="absolute -top-3 right-3 bg-primary text-primary-foreground"
                  >
                    {tier.trialLabel}
                  </Badge>
                )}

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "p-2 rounded-lg",
                        tier.popular ? "bg-primary/10" : "bg-muted"
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-5 w-5",
                          tier.popular ? "text-primary" : "text-muted-foreground"
                        )}
                      />
                    </div>
                    <h3 className="font-semibold text-lg">{tier.name}</h3>
                  </div>

                  <div>
                    <span className="text-3xl font-bold">${tier.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    {tier.description}
                  </p>

                  {tier.trialDays && (
                    <div className="rounded-md bg-primary/5 border border-primary/20 px-2 py-1.5 text-xs text-primary font-medium text-center">
                      Free for {tier.trialDays} days, then ${tier.price}/mo
                    </div>
                  )}

                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Discount Code Section */}
        <div className="flex flex-col items-center gap-3 pt-4 border-t">
          {!showDiscountInput ? (
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
              onClick={() => setShowDiscountInput(true)}
            >
              <Tag className="h-3 w-3" />
              Have a discount code?
            </button>
          ) : (
            <div className="w-full max-w-md space-y-2">
              <Label htmlFor="discount-code" className="text-sm">Discount Code</Label>
              <Input
                id="discount-code"
                placeholder="Enter discount code"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                className={cn(
                  isValidDiscount && "border-primary focus-visible:ring-primary"
                )}
              />
              {isValidDiscount && (
                <p className="text-sm text-primary flex items-center gap-1">
                  <Check className="h-4 w-4" />
                  100% discount applied - FREE access!
                </p>
              )}
            </div>
          )}

          <Button
            size="lg"
            className="w-full max-w-md"
            disabled={!selectedTier}
            onClick={handleContinue}
          >
            {isValidDiscount ? (
              <>Get Free Access</>
            ) : (
              <>
                Start{" "}
                {selectedTier
                  ? subscriptionTiers.find((t) => t.id === selectedTier)?.name
                  : "selected plan"}
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            {isValidDiscount
              ? "🎉 Your subscription will be completely free!"
              : "✨ No credit card required during trial • Cancel anytime"}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TierSelectionDialog;
